import {
  URL_ACCOUNT_HIRER_REGISTRATION,
  URL_ACCOUNT_SELECT,
} from '@seek/adv-constants';
import { useTrackLink } from '@seek/cmsu-analytics';
import { getLanguageFromLocale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Text,
  Card,
  Heading,
  ContentBlock,
  ButtonLink,
  Column,
  Columns,
  TextLinkButton,
} from 'braid-design-system';
import { Fragment, useEffect, useState, type UIEvent } from 'react';

import { analyticsEvents } from 'src/constants/analytics';
import { SELECT_ACCOUNT_PAGE_VIEW } from 'src/constants/pageViewUniqueKeys';
import { useConfig } from 'src/hooks/context';
import { useAuthenticatedViewTracking } from 'src/modules/AnalyticsProvider/useAuthenticatedViewTracking';
import getAccountSelection from 'src/modules/ApiClient/hirerGraph/queries/gql_getAccountSelection';
import getRedirectUrl from 'src/modules/ApiClient/hirerGraph/queries/gql_getRedirectUrl';
import type { AccountSelectionOptions } from 'src/modules/ApiClient/types';
import { getQueryParams } from 'src/utils/queryParams';
import SeoMeta from 'src/views/shared/SeoMeta/SeoMeta';

import translations from './.vocab';

import * as styles from './SelectAccountPage.css';

const stripOrigin = (url: string) => {
  if (url.indexOf(window.location.origin) === 0) {
    return url.substring(window.location.origin.length);
  }
  return url;
};

const SelectAccountPage = () => {
  const { absoluteEmployerUrlResolver, urlResolver, locale } = useConfig();
  const [userInfo] = useAuthenticatedViewTracking({
    eventName: SELECT_ACCOUNT_PAGE_VIEW,
  });

  const { t } = useTranslations(translations);
  const fullUrl = absoluteEmployerUrlResolver(URL_ACCOUNT_SELECT);

  const [accountSelection, setAccountSelection] =
    useState<AccountSelectionOptions>();

  const onClickLink = useTrackLink(SELECT_ACCOUNT_PAGE_VIEW, {
    eventName: analyticsEvents.TRACK_CREATE_NEW_ACCOUNT,
    entryPoint: 'account selection - create new account',
    ...userInfo,
  });

  useEffect(() => {
    const handleAccountSelectionPayload = async () => {
      const payload = await getAccountSelection();

      if (!payload) {
        return;
      }

      if (payload?.defaultAccount) {
        const { returnUrl: returnUri } = getQueryParams();
        const seekId = payload.defaultAccount.seekId;
        const redirectUrl = await getRedirectUrl({
          returnUri,
          hirerAccountId: seekId,
          language: locale,
        });
        window.location.assign(stripOrigin(redirectUrl));
      }

      if (payload?.accounts.length === 0) {
        window.location.assign(
          urlResolver({ path: URL_ACCOUNT_HIRER_REGISTRATION }),
        );
      }

      // sanitise redundant origins from gql provided hyperlinks
      setAccountSelection({
        accounts: payload.accounts.map((account) => ({
          ...account,
          childAccounts: account.childAccounts.map((childAccount) => ({
            ...childAccount,
          })),
        })),
      });
    };

    handleAccountSelectionPayload();
  }, [urlResolver, locale]);

  const accountOptions = accountSelection?.accounts || [];

  // render nothing until data has been fetched and number of accounts is not 1.
  // otherwise we will redirect the user directly to dashboard.
  // This logic aims to avoid content flickers.
  const renderContent = () => {
    if (accountSelection?.accounts) {
      if (accountSelection.accounts.length !== 1) {
        return true;
      }
      return accountSelection.accounts[0].childAccounts.length > 0;
    }
    return false;
  };

  const getReturnUri = () => {
    const { returnUrl: returnUri } = getQueryParams();
    const language = getLanguageFromLocale(locale);

    switch (language) {
      case 'en':
        return returnUri;
      default:
        if (returnUri) {
          return returnUri.match(/(th|id)/)
            ? returnUri
            : `/${language}${returnUri}`;
        }

        return `/${language}`;
    }
  };

  const handleSelectAccount = async (event: UIEvent, account: any) => {
    event.preventDefault();
    const seekId = account.seekId;

    try {
      const redirectUrl = await getRedirectUrl({
        returnUri: getReturnUri(),
        hirerAccountId: seekId,
        language: locale,
      });
      if (!redirectUrl) {
        throw new Error('Redirect url is undefined');
      }
      window.location.assign(stripOrigin(redirectUrl));
    } catch (error) {
      throw error;
    }
  };

  return renderContent() ? (
    <>
      <SeoMeta title={t('Browser tab title')} description={''} url={fullUrl} />
      <ContentBlock>
        <Box paddingBottom="xxlarge" paddingTop="large">
          <Box paddingBottom="small">
            <Box
              paddingTop={['medium', 'xlarge']}
              paddingBottom={'medium'}
              paddingX={['gutter', 'none']}
            >
              <Columns space="none">
                <Column>
                  <Box paddingTop="small">
                    <Heading level="2">{t('Select an account')}</Heading>
                  </Box>
                </Column>
                <Column width="content">
                  <ButtonLink
                    href={urlResolver({ path: URL_ACCOUNT_HIRER_REGISTRATION })}
                    onClick={() => (onClickLink ? onClickLink() : undefined)}
                  >
                    {t('Create new account')}
                  </ButtonLink>
                </Column>
              </Columns>
            </Box>
          </Box>
          <Card>
            {accountOptions.map((accountOption) => (
              <Fragment key={accountOption.advertiserId}>
                <Box className={styles.account}>
                  <Text size="large" key={accountOption.advertiserId}>
                    <TextLinkButton
                      onClick={(e) => handleSelectAccount(e, accountOption)}
                    >
                      {accountOption.displayText}
                    </TextLinkButton>
                  </Text>
                </Box>
                {accountOption.childAccounts.map((childAccountOption) => (
                  <Box
                    key={childAccountOption.advertiserId}
                    className={`${styles.account} ${styles.agency}`}
                  >
                    <Text size="large">
                      <TextLinkButton
                        onClick={(e) =>
                          handleSelectAccount(e, childAccountOption)
                        }
                      >
                        {childAccountOption.displayText}
                      </TextLinkButton>
                    </Text>
                  </Box>
                ))}
              </Fragment>
            ))}
          </Card>
        </Box>
      </ContentBlock>
    </>
  ) : null;
};

export default SelectAccountPage;
